/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
// RGBARRY-CUSTOMIZATION: reusable Page Designer landing pages at `/landing/:pageId`.
import type { ReactElement } from 'react';
import type { Route } from './+types/_app.landing.$pageId';
import { Region } from '@/components/region';
import { PageType } from '@/lib/decorators/page-type';
import { RegionDefinition } from '@/lib/decorators/region-definition';
import { fetchPageWithComponentData, type PageWithComponentData } from '@/lib/page-designer/page-loader.server';

@PageType({
    name: 'Landing Page',
    description: 'Flexible content landing page with the same authoring regions as the home page.',
    supportedAspectTypes: [],
})
@RegionDefinition([
    {
        id: 'main',
        name: 'Above the Fold',
        description: 'Up to two hero or promotional compositions prioritized for the initial page load',
        maxComponents: 2,
    },
    {
        id: 'restOfPage',
        name: 'Rest of Page',
        description: 'Below-the-fold content region — place remaining blocks in any order',
        maxComponents: 15,
    },
])
export class LandingPageMetadata {}

export type LandingPageData = {
    page: PageWithComponentData;
};

export async function loader(args: Route.LoaderArgs): Promise<LandingPageData> {
    const { pageId } = args.params;
    if (!pageId) {
        throw new Response('Not Found', { status: 404 });
    }

    const page = await fetchPageWithComponentData(args, { pageId });
    if (!page) {
        throw new Response('Not Found', { status: 404 });
    }

    return { page };
}

export const handle = {
    analytics: { routeType: 'content' },
};

export default function LandingPage({ loaderData }: { loaderData: LandingPageData }): ReactElement {
    return (
        <div className="pb-16 -mt-8">
            <Region
                page={loaderData.page}
                regionId="main"
                critical={true}
                fallbackElement={<div className="h-[87.5vh] bg-muted animate-pulse" />}
                errorElement={null}
            />
            <Region page={loaderData.page} regionId="restOfPage" errorElement={null} />
        </div>
    );
}
